import { FeatureTariffInformation } from "./FeatureTariffInformation";
import { createFeatureLockTranslator } from "./featureLockText";
import { ArrowLeftOutlined, CheckCircleOutlined, LockOutlined, RocketOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row, Space, Tag, Typography, theme } from "../ui";

import { spacing, typography } from "../layout/spacing";
import type { LockedFeaturePageProps } from "./featureLockTypes";

const { Title, Paragraph, Text } = Typography;

export function LockedFeaturePage({
  feature,
  onUpgrade,
  onBack,
  upgradeLabel,
  backLabel,
  benefitsTitle,
  tr,
  language,
}: LockedFeaturePageProps) {
  const { token } = theme.useToken();

  const translate = createFeatureLockTranslator(tr, language);

  const resolvedUpgradeLabel = upgradeLabel ?? translate("To plan selection", "zur Tarifauswahl");
  const resolvedBackLabel = backLabel ?? translate("Back", "zurück");
  const resolvedBenefitsTitle = benefitsTitle ?? translate("Included in this module:", "im Modul enthalten:");

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else if (feature.upgradeUrl && typeof window !== "undefined") {
      window.location.href = feature.upgradeUrl;
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
    }
  };

  return (
    <div
      style={{
        maxWidth: 860,
        margin: `${spacing.pageGap}px auto`,
        padding: `0 ${spacing.sectionGap}px`,
      }}
    >
      <Card
        style={{
          boxShadow: token.boxShadowSecondary,
          borderRadius: token.borderRadiusLG,
          border: `1px solid ${token.colorBorderSecondary}`,
          textAlign: "center",
          padding: `${spacing.sectionGap}px ${spacing.elementGap}px`,
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: token.borderRadiusLG,
            background: token.colorWarningBg,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: token.colorWarning,
            fontSize: typography.pageTitle,
            marginBottom: spacing.elementGap,
          }}
        >
          <LockOutlined />
        </div>

        {feature.badgeText && (
          <div style={{ marginBottom: spacing.elementGap }}>
            <Tag color="blue" style={{ fontSize: typography.sm, paddingInline: spacing.tightGap, paddingBlock: spacing.microGap }}>
              {feature.badgeText}
            </Tag>
          </div>
        )}

        <Title level={2} style={{ marginTop: 0, marginBottom: spacing.tightGap, fontSize: typography.pageTitle }}>
          {feature.title}
        </Title>

        <Paragraph
          style={{
            fontSize: typography.base,
            color: token.colorTextSecondary,
            maxWidth: 620,
            margin: `0 auto ${spacing.sectionGap}px`,
          }}
        >
          {feature.description}
        </Paragraph>

        {feature.highlights && feature.highlights.length > 0 && (
          <div
            style={{
              background: token.colorFillAlter,
              borderRadius: token.borderRadiusLG,
              padding: spacing.cardPadding,
              maxWidth: 640,
              margin: `0 auto ${spacing.sectionGap}px`,
              textAlign: "left",
            }}
          >
            <Text
              strong
              style={{
                display: "block",
                marginBottom: spacing.tightGap,
                fontSize: typography.base,
                color: token.colorText,
              }}
            >
              {resolvedBenefitsTitle}
            </Text>
            <Row gutter={[spacing.sectionGap, spacing.tightGap]}>
              {feature.highlights.map((h, i) => (
                <Col xs={24} sm={12} key={i}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: spacing.microGap }}>
                    <CheckCircleOutlined style={{ color: token.colorSuccess, marginTop: spacing.microGap, flexShrink: 0 }} />
                    <Text style={{ fontSize: typography.base }}>{h}</Text>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {(feature.planName || feature.addonName) && (
          <div
            style={{
              background: token.colorFillAlter,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
              padding: spacing.cardPadding,
              maxWidth: 640,
              margin: `0 auto ${spacing.sectionGap}px`,
              textAlign: "left",
            }}
          >
            <Text
              strong
              style={{
                display: "block",
                marginBottom: spacing.tightGap,
                fontSize: typography.base,
                color: token.colorText,
              }}
            >
              {translate("Tariff and booking information", "Tarif- und Buchungsinformation")}
            </Text>
            <FeatureTariffInformation feature={feature} translate={translate} />
          </div>
        )}

        <Space size={spacing.elementGap} wrap style={{ justifyContent: "center" }}>
          <Button size="large" icon={<ArrowLeftOutlined />} onClick={handleBack}>
            {resolvedBackLabel}
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<RocketOutlined />}
            onClick={handleUpgrade}
          >
            {resolvedUpgradeLabel}
          </Button>
        </Space>
      </Card>
    </div>
  );
}
